export const SESSION_ORIGINS = Object.freeze({
  NONE: "none",
  NEW: "new",
  HISTORY: "history",
  URL: "url",
  CURRENT: "current_session",
});

export const SESSION_LISTENER_STATES = Object.freeze({
  NEW_UNPERSISTED: "new_unpersisted",
  INITIAL_PARENT_WRITE_IN_PROGRESS: "initial_parent_write_in_progress",
  INITIAL_PARENT_WRITE_CONFIRMED: "initial_parent_write_confirmed",
  EXISTING_DOCUMENT_SELECTED: "existing_document_selected",
  LISTENER_ATTACHED: "listener_attached",
  LISTENER_FAILED: "listener_failed",
  LISTENER_DETACHED: "listener_detached",
});

function normalizedScope(orgId = "", sessionId = "") {
  const normalizedOrgId = String(orgId || "").trim();
  const normalizedSessionId = String(sessionId || "").trim();
  return {
    orgId: normalizedOrgId,
    sessionId: normalizedSessionId,
    scopeKey:
      normalizedOrgId && normalizedSessionId
        ? `${normalizedOrgId}:${normalizedSessionId}`
        : "",
  };
}

export function createSessionListenerLifecycle() {
  return {
    ...normalizedScope(),
    origin: SESSION_ORIGINS.NONE,
    state: SESSION_LISTENER_STATES.LISTENER_DETACHED,
    documentReady: false,
    initialWriteOperationId: "",
    initialWriteKind: "",
    initialWriteStartedAt: null,
    initialWriteConfirmedAt: null,
    initialWriteFailedAt: null,
    listenerAttempt: 0,
    listenerAttachedAt: null,
    listenerFailedAt: null,
    listenerDetachedAt: null,
    listenerErrorCode: "",
    recoveryAttempted: false,
  };
}

function eventMatchesScope(current, event) {
  return Boolean(
    current.scopeKey &&
    current.scopeKey === normalizedScope(event.orgId, event.sessionId).scopeKey
  );
}

export function transitionSessionListenerLifecycle(current, event = {}) {
  const state = current || createSessionListenerLifecycle();
  const at = event.at || new Date().toISOString();

  switch (event.type) {
    case "select_new": {
      const scope = normalizedScope(event.orgId, event.sessionId);
      if (!scope.scopeKey) return createSessionListenerLifecycle();
      return {
        ...createSessionListenerLifecycle(),
        ...scope,
        origin: SESSION_ORIGINS.NEW,
        state: SESSION_LISTENER_STATES.NEW_UNPERSISTED,
      };
    }
    case "select_existing": {
      const scope = normalizedScope(event.orgId, event.sessionId);
      if (!scope.scopeKey) return createSessionListenerLifecycle();
      return {
        ...createSessionListenerLifecycle(),
        ...scope,
        origin: [
          SESSION_ORIGINS.HISTORY,
          SESSION_ORIGINS.URL,
          SESSION_ORIGINS.CURRENT,
        ].includes(event.origin)
          ? event.origin
          : SESSION_ORIGINS.URL,
        state: SESSION_LISTENER_STATES.EXISTING_DOCUMENT_SELECTED,
        documentReady: true,
      };
    }
    case "clear":
      return {
        ...createSessionListenerLifecycle(),
        listenerDetachedAt: at,
      };
    case "initial_write_started":
      if (
        !eventMatchesScope(state, event) ||
        state.origin !== SESSION_ORIGINS.NEW ||
        state.documentReady ||
        state.state !== SESSION_LISTENER_STATES.NEW_UNPERSISTED ||
        !String(event.operationId || "")
      ) {
        return state;
      }
      return {
        ...state,
        state: SESSION_LISTENER_STATES.INITIAL_PARENT_WRITE_IN_PROGRESS,
        initialWriteOperationId: String(event.operationId),
        initialWriteKind: String(event.writeKind || "parent_set_doc"),
        initialWriteStartedAt: at,
        initialWriteFailedAt: null,
      };
    case "initial_write_confirmed":
      if (
        !eventMatchesScope(state, event) ||
        state.origin !== SESSION_ORIGINS.NEW ||
        state.state !==
          SESSION_LISTENER_STATES.INITIAL_PARENT_WRITE_IN_PROGRESS ||
        String(event.operationId || "") !== state.initialWriteOperationId
      ) {
        return state;
      }
      return {
        ...state,
        state: SESSION_LISTENER_STATES.INITIAL_PARENT_WRITE_CONFIRMED,
        documentReady: true,
        initialWriteConfirmedAt: at,
        listenerAttempt: state.listenerAttempt + 1,
      };
    case "initial_write_failed":
      if (
        !eventMatchesScope(state, event) ||
        state.origin !== SESSION_ORIGINS.NEW ||
        String(event.operationId || "") !== state.initialWriteOperationId
      ) {
        return state;
      }
      return {
        ...state,
        state: SESSION_LISTENER_STATES.NEW_UNPERSISTED,
        initialWriteOperationId: "",
        initialWriteKind: "",
        initialWriteFailedAt: at,
      };
    case "listener_attached":
      if (!eventMatchesScope(state, event) || !state.documentReady) return state;
      return {
        ...state,
        state: SESSION_LISTENER_STATES.LISTENER_ATTACHED,
        listenerAttachedAt: at,
        listenerFailedAt: null,
        listenerErrorCode: "",
      };
    case "listener_failed":
      if (!eventMatchesScope(state, event) || !state.documentReady) return state;
      return {
        ...state,
        state: SESSION_LISTENER_STATES.LISTENER_FAILED,
        listenerFailedAt: at,
        listenerErrorCode: String(event.errorCode || ""),
      };
    case "retry_listener":
      if (
        !eventMatchesScope(state, event) ||
        state.origin !== SESSION_ORIGINS.NEW ||
        !state.documentReady ||
        state.recoveryAttempted ||
        state.state !== SESSION_LISTENER_STATES.LISTENER_FAILED
      ) {
        return state;
      }
      return {
        ...state,
        state: SESSION_LISTENER_STATES.INITIAL_PARENT_WRITE_CONFIRMED,
        recoveryAttempted: true,
        listenerAttempt: state.listenerAttempt + 1,
      };
    case "listener_detached":
      if (!eventMatchesScope(state, event)) return state;
      return {
        ...state,
        state:
          state.state === SESSION_LISTENER_STATES.LISTENER_FAILED
            ? SESSION_LISTENER_STATES.LISTENER_FAILED
            : SESSION_LISTENER_STATES.LISTENER_DETACHED,
        listenerDetachedAt: at,
      };
    default:
      return state;
  }
}

export function isSessionListenerGateOpen(
  lifecycle,
  { orgId = "", sessionId = "" } = {}
) {
  const scope = normalizedScope(orgId, sessionId);
  return Boolean(
    lifecycle?.documentReady === true &&
    lifecycle?.scopeKey &&
    lifecycle.scopeKey === scope.scopeKey &&
    lifecycle.state !== SESSION_LISTENER_STATES.LISTENER_FAILED
  );
}

export function shouldStartInitialParentWrite(
  lifecycle,
  { orgId = "", sessionId = "" } = {}
) {
  const scope = normalizedScope(orgId, sessionId);
  return Boolean(
    lifecycle?.origin === SESSION_ORIGINS.NEW &&
    lifecycle?.state === SESSION_LISTENER_STATES.NEW_UNPERSISTED &&
    lifecycle?.scopeKey === scope.scopeKey
  );
}
